import {MIN_INPUT_WIDTH} from '@github-ui/filter-core'
import {testIdProps} from '@github-ui/test-id-props'
import {useFormControlForwardedProps} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef} from 'react'

import {useInputDispatch, useInputState, useSuggestionsState} from '../context'
import styles from './Input.module.css'
import {StyledInput} from './StyledInput'

type FilterInputProps = {
  id: string
  hasValidationMessage?: boolean
  placeholder?: string
  onKeyDown?: React.KeyboardEventHandler
}

export const Input = ({
  id,
  hasValidationMessage = false,
  placeholder = 'Search or filter',
  onKeyDown,
}: FilterInputProps) => {
  const {suggestionsVisible, activeSuggestion, suggestionGroups} = useSuggestionsState()
  const {inputFocused, inputSelectionEnd, inputSelectionStart, inputValue} = useInputState()
  const {
    caretRef,
    inputKeyDown,
    inputOnBlur,
    inputOnCompositionStart,
    inputOnCompositionEnd,
    inputOnChange,
    inputOnFocus,
    inputRef,
    updateInputSelection,
  } = useInputDispatch()
  const sizerRef = useRef<HTMLDivElement>(null)
  const scrollLeftRef = useRef(0)
  const styledInputContainerRef = useRef<HTMLDivElement>(null)

  const checkCursorPosition = useCallback(
    (el: HTMLInputElement) => {
      updateInputSelection(el.selectionStart ?? 0, el.selectionEnd ?? 0)
    },
    [updateInputSelection],
  )

  const onKeyDownHandler = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    e => {
      checkCursorPosition(e.currentTarget)
      inputKeyDown(e)
      onKeyDown?.(e)
    },
    [checkCursorPosition, inputKeyDown, onKeyDown],
  )

  const onClickHandler = useCallback<React.MouseEventHandler<HTMLInputElement>>(
    e => {
      checkCursorPosition(e.currentTarget)
    },
    [checkCursorPosition],
  )

  const hasMountedRef = useRef(false)

  // Combined layout pass for input width + cursor scrolling.
  // All layout reads happen before any layout writes, so the browser performs at most one
  // forced reflow per keystroke. Both values are written imperatively (no state, no re-render).
  useLayoutEffect(() => {
    const sizer = sizerRef.current
    const input = inputRef.current
    const cursor = caretRef.current
    const styledInputContainer = styledInputContainerRef.current

    if (!sizer || !input) return

    // --- READS ---
    const sizerScrollWidth = sizer.scrollWidth
    // Skip cursor measurement on initial mount — the filter input is not scrolled yet,
    // so reading offsetLeft / clientWidth would trigger an expensive forced reflow
    // on the freshly-rendered (dirty) DOM for no visual benefit.
    let cursorOffsetLeft: number | null = null
    let containerClientWidth = 0
    if (hasMountedRef.current && cursor && styledInputContainer) {
      cursorOffsetLeft = cursor.offsetLeft
      containerClientWidth = styledInputContainer.clientWidth
    }
    hasMountedRef.current = true

    // --- WRITES ---
    // scrollLeft is written before style.width because the lint rule treats scrollLeft
    // assignment as a layout-affecting access, and we want all layout reads/scrollLeft
    // ops to complete before any layout-invalidating style writes.
    if (cursorOffsetLeft !== null && styledInputContainer) {
      let newScrollLeft = scrollLeftRef.current
      if (cursorOffsetLeft < scrollLeftRef.current) {
        // cursor is out of view to the left
        newScrollLeft = cursorOffsetLeft - containerClientWidth - MIN_INPUT_WIDTH
      } else if (cursorOffsetLeft > scrollLeftRef.current + containerClientWidth) {
        // cursor is out of view to the right
        newScrollLeft = cursorOffsetLeft - containerClientWidth + MIN_INPUT_WIDTH
      }
      scrollLeftRef.current = newScrollLeft
      styledInputContainer.scrollLeft = newScrollLeft
    }

    const newInputWidth = Math.max(sizerScrollWidth + 2, MIN_INPUT_WIDTH)
    input.style.width = `${newInputWidth}px`
  }, [inputSelectionStart, inputValue, inputRef, caretRef])

  const shadowInput = useMemo(() => {
    return (
      <>
        <span>{inputSelectionStart ? inputValue.substring(0, inputSelectionStart) : inputValue}</span>
        <span {...testIdProps('filter-cursor')} ref={caretRef} />
        <span>{inputSelectionStart ? inputValue.substring(inputSelectionStart) : null}</span>
      </>
    )
  }, [caretRef, inputSelectionStart, inputValue])

  const ariaExpandedValue = useMemo(() => {
    return suggestionsVisible && suggestionGroups.some(s => s.suggestions.length > 0)
  }, [suggestionGroups, suggestionsVisible])

  const applySelectionToInput = useEffectEvent((selectionStart: number, selectionEnd: number) => {
    if (inputRef.current && selectionStart > -1 && inputFocused) {
      inputRef.current.selectionStart = selectionStart
      inputRef.current.selectionEnd = selectionEnd
    }
  })

  useEffect(() => {
    applySelectionToInput(inputSelectionStart, inputSelectionEnd)
    // inputValue is a trigger: the controlled <input value> re-applies to the DOM and resets selection,
    // so we re-restore it here even though we don't read inputValue directly.
  }, [inputSelectionStart, inputSelectionEnd, inputValue])

  const {'aria-describedby': formDescribedBy, ...formControlRest} = useFormControlForwardedProps({})

  const filterDescribedBy = hasValidationMessage ? `${id}-validation-message` : ''
  const ariaDescribedBy = [formDescribedBy, filterDescribedBy].filter(Boolean).join(' ')

  return (
    <div
      {...testIdProps('styled-input-container')}
      ref={styledInputContainerRef}
      tabIndex={-1}
      className={clsx('styled-input-container', styles.Box_0)}
    >
      <div
        {...testIdProps('styled-input-content')}
        aria-hidden="true"
        className={clsx('styled-input-content', styles.Box_1)}
      >
        {/* This is a visual-only component and is intentionally hidden from screen readers.
        It will use the Input below instead */}
        <StyledInput />
      </div>
      <div {...testIdProps('filter-input-wrapper')} className={styles.Box_2}>
        <div {...testIdProps('filter-sizer')} ref={sizerRef} aria-hidden="true" className={styles.Box_3}>
          {shadowInput}
        </div>
        <input
          id={`${id}-input`}
          role="combobox"
          aria-expanded={ariaExpandedValue}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-controls={`${id}-results`}
          aria-activedescendant={
            activeSuggestion !== null && activeSuggestion !== -1 && suggestionsVisible
              ? `suggestion-${activeSuggestion}`
              : undefined
          }
          aria-describedby={ariaDescribedBy}
          placeholder={placeholder}
          ref={inputRef}
          value={inputValue}
          onFocus={inputOnFocus}
          onBlur={inputOnBlur}
          onCompositionStart={inputOnCompositionStart}
          onCompositionEnd={inputOnCompositionEnd}
          onKeyDown={onKeyDownHandler}
          onClick={onClickHandler}
          onChange={inputOnChange}
          name={`${id}-inputname`}
          autoComplete="off"
          spellCheck="false"
          className={styles.Box_4}
          {...testIdProps('filter-input')}
          {...formControlRest}
        />
      </div>
    </div>
  )
}
